
import React from 'react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { cva, type VariantProps } from "class-variance-authority";

const buttonVariants = cva(
  "inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-teal whitespace-nowrap",
  {
    variants: {
      variant: {
        primary: "bg-gradient-to-r from-brand-teal to-brand-purple text-white hover:shadow-lg hover:translate-y-[-1px]",
        secondary: "bg-brand-blue text-white hover:shadow-md hover:bg-opacity-90",
        outline: "border-2 border-brand-teal text-brand-teal bg-transparent hover:bg-brand-teal/10",
        text: "bg-transparent text-brand-blue hover:text-brand-teal underline-offset-4 hover:underline",
        ghost: "bg-transparent hover:bg-gray-100 text-gray-700 hover:text-gray-900",
        default: "bg-gradient-to-r from-brand-teal to-brand-purple text-white hover:shadow-lg hover:translate-y-[-1px]",
      },
      size: {
        sm: "text-sm px-4 py-2 rounded-md",
        md: "text-base px-6 py-3 rounded-md",
        lg: "text-lg px-8 py-4 rounded-lg",
        icon: "h-9 w-9 p-0 rounded-md",
        default: "text-base px-6 py-3 rounded-md", // Added 'default' size
      },
      fullWidth: {
        true: "w-full",
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      fullWidth: false,
    },
  }
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'text' | 'ghost' | 'default';
  size?: 'sm' | 'md' | 'lg' | 'icon' | 'default';
  className?: string;
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (event?: any) => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  href,
  type = 'button',
  onClick,
  disabled = false,
  icon,
  iconPosition = 'right',
  fullWidth = false,
  ...props
}, ref) => {
  const baseStyles = 'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-teal whitespace-nowrap';
  
  const variantStyles = {
    primary: 'bg-gradient-to-r from-brand-teal to-brand-purple text-white hover:shadow-lg hover:translate-y-[-1px]',
    secondary: 'bg-brand-blue text-white hover:shadow-md hover:bg-opacity-90',
    outline: 'border-2 border-brand-teal text-brand-teal bg-transparent hover:bg-brand-teal/10',
    text: 'bg-transparent text-brand-blue hover:text-brand-teal underline-offset-4 hover:underline',
    ghost: 'bg-transparent hover:bg-gray-100 text-gray-700 hover:text-gray-900',
    default: 'bg-gradient-to-r from-brand-teal to-brand-purple text-white hover:shadow-lg hover:translate-y-[-1px]',
  };
  
  const sizeStyles = {
    sm: 'text-sm px-4 py-2 rounded-md',
    md: 'text-base px-6 py-3 rounded-md',
    lg: 'text-lg px-8 py-4 rounded-lg',
    icon: 'h-9 w-9 p-0 rounded-md',
    default: 'text-base px-6 py-3 rounded-md',
  };
  
  const buttonStyles = cn(
    baseStyles,
    variantStyles[variant],
    sizeStyles[size],
    fullWidth && 'w-full',
    disabled && 'opacity-50 cursor-not-allowed',
    className
  );
  
  const contentWithIcon = (
    <>
      {icon && iconPosition === 'left' && (
        <span className="mr-2">{icon}</span>
      )}
      {children}
      {icon && iconPosition === 'right' && (
        <span className="ml-2">{icon}</span>
      )}
    </>
  );
  
  if (href) {
    return (
      <Link to={href} className={buttonStyles}>
        {contentWithIcon}
      </Link>
    );
  }
  
  return (
    <button
      ref={ref}
      type={type}
      className={buttonStyles}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {contentWithIcon}
    </button>
  );
});

Button.displayName = "Button";

export { Button, buttonVariants };
export default Button;
